import React, { useState } from 'react';
import { Box, Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';

// Import child components
import EmployeeDashboard from './EmployeeDashboard';
import EmployeesList from './EmployeesList';

const EmployeeMaster = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const [activeMenu, setActiveMenu] = useState('employees');

  // NavGroupHeader - USING THEME STYLES (same as Dashboard)
  const NavGroupHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  // NavItem - USING THEME STYLES (same as Dashboard)
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeMenu === id} 
        onClick={() => setActiveMenu(id)}
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
          '&.Mui-selected .MuiListItemIcon-root': { color: 'white' }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeMenu === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeMenu === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop
    }}>
      
      {/* LEFT SIDEBAR - Same styling as Dashboard */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <NavGroupHeader title="EMPLOYEES" />
          <NavItem id="employees" label="Employees" icon={<PeopleIcon fontSize="small" />} />
          <NavItem id="dashboard" label="Dashboard" icon={<DashboardIcon fontSize="small" />} />

        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Same styling as Dashboard */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        width: '100%', 
        bgcolor: '#fbfbfb', 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        {activeMenu === 'dashboard' && <EmployeeDashboard />}
        {activeMenu === 'employees' && <EmployeesList />}
      </Box>

    </Paper>
  );
};

export default EmployeeMaster;